import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic } from "lucide-react";

import { audioService } from "@/core/audio/audioService";

/**
 * SixSevenFX — Phase 2 special signature for the "Six Seven" number (67).
 *
 * The signature layers ON TOP of the standard ball reveal — the ball
 * still spins, opens, and unveils its sacred 67 exactly as usual. This
 * component only decorates the reveal moment:
 *
 *   0.00s  "SIX"  slams in from the LEFT (large, deep red, italic)
 *   0.40s  suspense hold (dramatic pause)
 *   0.55s  "SEVEN!" slams in from the RIGHT (larger, bright gold,
 *          exclamation emphasis, glowing)
 *   0.90s  both settle together as "SIX · SEVEN" above the ball
 *          while the magic trail fires
 *   1.60s  overlay fades and unmounts
 *
 * Audio: two-beat "thud ... POP!" via audioService.playSixSeven()
 * mirroring the visual rhythm.
 *
 * Rules honored:
 *   • Number NEVER shown before the ball opens (the ball itself owns
 *     that timing; this component only draws text after Stage D).
 *   • Revealed number is SACRED — the ball's 67 never morphs.
 *   • Layered above the standard reveal, doesn't touch the ball logic.
 */

export const SIX_SEVEN_VALUE = 67;
export const SIX_SEVEN_DURATION_MS = 1600;

export default function SixSevenFX({ active }) {
  useEffect(() => {
    if (!active) return;
    audioService.playSixSeven();
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="phase2-six-seven-fx"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
        >
          {/* Dramatic scrim behind text so words pop against the ball */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.55, 0.35, 0] }}
            transition={{
              duration: SIX_SEVEN_DURATION_MS / 1000,
              times: [0, 0.15, 0.7, 1],
              ease: "easeOut",
            }}
            style={{
              background:
                "radial-gradient(ellipse 60% 70% at 50% 50%, rgba(0,0,0,0.55) 0%, transparent 80%)",
            }}
          />

          {/* Wrapper — text sits ABOVE the ball so the sacred number stays visible */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            style={{ transform: "translateY(-60%)" }}
          >
            <div className="flex items-center gap-6 whitespace-nowrap">
              {/* SIX — deep red, italic, from the left */}
              <motion.span
                data-testid="phase2-six-seven-six"
                className="ic-font-display font-black italic select-none"
                style={{
                  fontSize: "clamp(4rem, 8vw, 9rem)",
                  color: "#DC2626",
                  textShadow:
                    "0 0 30px rgba(220, 38, 38, 0.65), 0 0 60px rgba(220, 38, 38, 0.35)",
                  letterSpacing: "-0.02em",
                }}
                initial={{ x: -260, opacity: 0, scale: 0.6, rotate: -6 }}
                animate={{
                  x: [-260, 20, 0],
                  opacity: [0, 1, 1, 1, 0.85],
                  scale: [0.6, 1.15, 1, 1, 1],
                  rotate: [-6, 2, 0, 0, 0],
                }}
                transition={{
                  duration: SIX_SEVEN_DURATION_MS / 1000,
                  times: [0, 0.18, 0.3, 0.7, 1],
                  ease: "easeOut",
                }}
              >
                SIX
              </motion.span>

              {/* Ellipsis — the dramatic suspense breath */}
              <motion.span
                className="ic-font-display font-black tracking-widest"
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 5rem)",
                  color: "rgba(255,255,255,0.55)",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0, 1, 1, 0] }}
                transition={{
                  duration: SIX_SEVEN_DURATION_MS / 1000,
                  times: [0, 0.22, 0.32, 0.7, 1],
                }}
              >
                ...
              </motion.span>

              {/* SEVEN! — bigger, bright gold, from the right (arrives on beat 2) */}
              <motion.span
                data-testid="phase2-six-seven-seven"
                className="ic-font-display font-black select-none flex items-baseline"
                style={{
                  fontSize: "clamp(4.5rem, 9vw, 10.5rem)",
                  color: "#FDE68A",
                  textShadow:
                    "0 0 40px rgba(245, 158, 11, 0.9), 0 0 90px rgba(245, 158, 11, 0.55), 0 6px 20px rgba(0,0,0,0.6)",
                  letterSpacing: "-0.02em",
                }}
                initial={{ x: 260, opacity: 0, scale: 0.4, rotate: 6 }}
                animate={{
                  x: [260, -20, 0],
                  opacity: [0, 0, 1, 1, 0.9],
                  scale: [0.4, 0.4, 1.35, 1, 1],
                  rotate: [6, 6, -3, 0, 0],
                }}
                transition={{
                  duration: SIX_SEVEN_DURATION_MS / 1000,
                  times: [0, 0.34, 0.5, 0.66, 1],
                  ease: "easeOut",
                }}
              >
                SEVEN
                <motion.span
                  aria-hidden
                  style={{ display: "inline-block" }}
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: [0.4, 1.6, 1], opacity: [0, 1, 1] }}
                  transition={{
                    duration: SIX_SEVEN_DURATION_MS / 1000,
                    times: [0.34, 0.5, 0.66],
                    ease: "easeOut",
                  }}
                >
                  !
                </motion.span>
              </motion.span>
            </div>

            {/* Microphone icons flanking — subtle "vocal moment" cue */}
            <motion.div
              className="flex items-center gap-4 mt-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: [0, 0, 0.85, 0.85, 0],
                y: [-10, -10, 0, 0, 0],
              }}
              transition={{
                duration: SIX_SEVEN_DURATION_MS / 1000,
                times: [0, 0.32, 0.5, 0.75, 1],
                ease: "easeOut",
              }}
            >
              <Mic
                className="w-6 h-6"
                style={{
                  color: "#FDE68A",
                  filter: "drop-shadow(0 0 12px rgba(245, 158, 11, 0.9))",
                }}
                strokeWidth={2.5}
              />
              <span
                className="text-[10px] tracking-[0.5em] uppercase"
                style={{ color: "#FDE68A" }}
              >
                Six Seven
              </span>
              <Mic
                className="w-6 h-6"
                style={{
                  color: "#FDE68A",
                  filter: "drop-shadow(0 0 12px rgba(245, 158, 11, 0.9))",
                  transform: "scaleX(-1)",
                }}
                strokeWidth={2.5}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
